import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of, forkJoin } from 'rxjs';
import { map, catchError, switchMap, filter } from 'rxjs/operators';
import { ReferenceRepository } from '../../infrastructure/http/repositories/reference.repository';
import { LoggingService } from '../../core/services/logging.service';
import { ReferenceActions, ReferenceAPIActions } from './reference.actions';
import {
  selectPrintSitesLoaded,
  selectCustomerTypesLoaded,
  selectCheckLayoutsLoaded,
} from './reference.selectors';

@Injectable()
export class ReferenceEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly repository = inject(ReferenceRepository);
  private readonly logger = inject(LoggingService);

  loadAllReferenceData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferenceActions.loadAllReferenceData),
      concatLatestFrom(() => [
        this.store.select(selectPrintSitesLoaded),
        this.store.select(selectCustomerTypesLoaded),
        this.store.select(selectCheckLayoutsLoaded),
      ]),
      switchMap(
        ([, printSitesLoaded, customerTypesLoaded, checkLayoutsLoaded]) => {
          this.logger.debug('Loading all reference data');

          const requests = [];

          if (!printSitesLoaded) {
            requests.push(
              this.repository.getPrintSites().pipe(
                map(printSites =>
                  ReferenceAPIActions.loadPrintSitesSuccess({ printSites })
                ),
                catchError(error => {
                  this.logger.error('Failed to load print sites', error);
                  return of(
                    ReferenceAPIActions.loadPrintSitesFailure({
                      error: 'Failed to load print sites.',
                    })
                  );
                })
              )
            );
          }

          if (!customerTypesLoaded) {
            requests.push(
              this.repository.getCustomerTypes().pipe(
                map(customerTypes =>
                  ReferenceAPIActions.loadCustomerTypesSuccess({
                    customerTypes,
                  })
                ),
                catchError(error => {
                  this.logger.error('Failed to load customer types', error);
                  return of(
                    ReferenceAPIActions.loadCustomerTypesFailure({
                      error: 'Failed to load customer types.',
                    })
                  );
                })
              )
            );
          }

          if (!checkLayoutsLoaded) {
            requests.push(
              this.repository.getCheckLayouts().pipe(
                map(checkLayouts =>
                  ReferenceAPIActions.loadCheckLayoutsSuccess({ checkLayouts })
                ),
                catchError(error => {
                  this.logger.error('Failed to load check layouts', error);
                  return of(
                    ReferenceAPIActions.loadCheckLayoutsFailure({
                      error: 'Failed to load check layouts.',
                    })
                  );
                })
              )
            );
          }

          if (requests.length === 0) {
            return of();
          }

          return forkJoin(requests).pipe(switchMap(actions => actions));
        }
      )
    )
  );

  loadPrintSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferenceActions.loadPrintSites),
      concatLatestFrom(() => this.store.select(selectPrintSitesLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.repository.getPrintSites().pipe(
          map(printSites =>
            ReferenceAPIActions.loadPrintSitesSuccess({ printSites })
          ),
          catchError(error => {
            this.logger.error('Failed to load print sites', error);
            return of(
              ReferenceAPIActions.loadPrintSitesFailure({
                error: 'Failed to load print sites.',
              })
            );
          })
        )
      )
    )
  );

  loadCustomerTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferenceActions.loadCustomerTypes),
      concatLatestFrom(() => this.store.select(selectCustomerTypesLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.repository.getCustomerTypes().pipe(
          map(customerTypes =>
            ReferenceAPIActions.loadCustomerTypesSuccess({ customerTypes })
          ),
          catchError(error => {
            this.logger.error('Failed to load customer types', error);
            return of(
              ReferenceAPIActions.loadCustomerTypesFailure({
                error: 'Failed to load customer types.',
              })
            );
          })
        )
      )
    )
  );

  loadCheckLayouts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferenceActions.loadCheckLayouts),
      concatLatestFrom(() => this.store.select(selectCheckLayoutsLoaded)),
      filter(([, loaded]) => !loaded),
      switchMap(() =>
        this.repository.getCheckLayouts().pipe(
          map(checkLayouts =>
            ReferenceAPIActions.loadCheckLayoutsSuccess({ checkLayouts })
          ),
          catchError(error => {
            this.logger.error('Failed to load check layouts', error);
            return of(
              ReferenceAPIActions.loadCheckLayoutsFailure({
                error: 'Failed to load check layouts.',
              })
            );
          })
        )
      )
    )
  );
}
